import { ENVELOPE_DURATION_CAP_MINUTES, ENVELOPE_DURATION_INCREMENT_MINUTES } from "./envelope-duration";
import { PreconditionFailedError } from "./errors";

/** Plain minute-based shape accepted and returned by domain model boundaries. */
export interface StepPlacementProps {
  offsetMinutes: number;
  durationMinutes: number;
}

/** An authored placement may omit duration to use one scheduling increment. */
export interface StepPlacementInput {
  offsetMinutes: number;
  durationMinutes?: number;
}

/** New steps default to one scheduling increment. */
export function resolvePlacement(input: StepPlacementInput): StepPlacement {
  return new StepPlacement({
    offsetMinutes: input.offsetMinutes,
    durationMinutes: input.durationMinutes === undefined ? ENVELOPE_DURATION_INCREMENT_MINUTES : input.durationMinutes,
  });
}

/** A step's own duration and offset. Zero is reserved for released historical work. */
export class StepPlacement {
  readonly #offsetMinutes: number;
  readonly #durationMinutes: number;

  constructor(props: StepPlacementProps) {
    const { offsetMinutes, durationMinutes } = props;
    if (!Number.isFinite(offsetMinutes) || !Number.isFinite(durationMinutes)) {
      throw new PreconditionFailedError("Step placement requires finite offset and duration minutes");
    }
    if (offsetMinutes < 0) {
      throw new PreconditionFailedError("Offset must be non-negative");
    }
    if (durationMinutes < 0) {
      throw new PreconditionFailedError("Duration must be non-negative");
    }
    if (
      offsetMinutes % ENVELOPE_DURATION_INCREMENT_MINUTES !== 0 ||
      durationMinutes % ENVELOPE_DURATION_INCREMENT_MINUTES !== 0
    ) {
      throw new PreconditionFailedError(
        "Offset and duration must be exact multiples of the envelope duration increment",
      );
    }
    if (offsetMinutes + durationMinutes > ENVELOPE_DURATION_CAP_MINUTES) {
      throw new PreconditionFailedError("Step end must not exceed the envelope duration cap");
    }
    this.#offsetMinutes = offsetMinutes;
    this.#durationMinutes = durationMinutes;
  }

  get offsetMinutes(): number {
    return this.#offsetMinutes;
  }

  get durationMinutes(): number {
    return this.#durationMinutes;
  }

  get endMinutes(): number {
    return this.#offsetMinutes + this.#durationMinutes;
  }

  overlaps(other: StepPlacement): boolean {
    return (
      this.#durationMinutes > 0 &&
      other.durationMinutes > 0 &&
      this.#offsetMinutes < other.endMinutes &&
      other.#offsetMinutes < this.endMinutes
    );
  }

  precedes(other: StepPlacement): boolean {
    return this.endMinutes <= other.#offsetMinutes;
  }

  fitsWithin(envelopeMinutes: number): boolean {
    return this.endMinutes <= envelopeMinutes;
  }

  shiftedBy(minutes: number): StepPlacement {
    return new StepPlacement({ offsetMinutes: this.#offsetMinutes + minutes, durationMinutes: this.#durationMinutes });
  }

  resizedTo(durationMinutes: number): StepPlacement {
    if (durationMinutes <= 0) throw new PreconditionFailedError("Resizing requires positive duration");
    return new StepPlacement({ offsetMinutes: this.#offsetMinutes, durationMinutes });
  }
}

/** Release withdrawn steps without deleting their orders or consuming authored gaps. */
export function releaseStepTime<
  T extends {
    placement: StepPlacementProps;
    orders: readonly { status: string }[];
  },
>(steps: readonly T[], removed: ReadonlySet<number>, envelopeMinutes: number): { steps: T[]; envelopeMinutes: number } {
  let released = 0;
  const result = steps.map((step, index) => {
    // Delivered work is a fixed boundary. Time before it cannot pull it earlier.
    if (step.orders.some((order) => order.status === "completed")) released = 0;
    const placement = {
      offsetMinutes: step.placement.offsetMinutes - released,
      durationMinutes: step.placement.durationMinutes,
    };
    if (removed.has(index)) {
      released += placement.durationMinutes;
      placement.durationMinutes = 0;
    }
    return { ...step, placement };
  });
  return {
    steps: result,
    envelopeMinutes: Math.max(
      ENVELOPE_DURATION_INCREMENT_MINUTES,
      envelopeMinutes - released,
      ...result.map((step) => step.placement.offsetMinutes + step.placement.durationMinutes),
    ),
  };
}

/**
 * Settle collisions on both sides of an already-edited step with minimal displacement.
 * Later steps move flush after their predecessor; earlier steps move flush
 * before their successor. The first gap in each direction stops that cascade.
 * Authored gaps may be consumed. This loss is deliberate: an author can restore
 * spacing by dragging later steps out again.
 *
 * Input offsets must be non-descending. Candidates are immutable: if settling
 * would cross offset zero or the end cap, no input placement has been changed.
 */
export function settle<T extends { placement: StepPlacement }>(steps: readonly T[], fromIndex: number): T[] {
  if (!Number.isInteger(fromIndex) || fromIndex < 0 || fromIndex >= steps.length) {
    throw new PreconditionFailedError("Settle requires an integer fromIndex within the steps array");
  }
  let previousOffset = -Infinity;
  for (const step of steps) {
    if (step.placement.offsetMinutes < previousOffset) {
      throw new PreconditionFailedError("Settle requires steps in non-descending offset order");
    }
    previousOffset = step.placement.offsetMinutes;
  }
  const result = [...steps];
  const changed = steps[fromIndex];
  if (!changed) throw new PreconditionFailedError("Settle requires a step at fromIndex");
  let successor = changed;
  for (const [reverseIndex, step] of steps.slice(0, fromIndex).reverse().entries()) {
    if (step.placement.precedes(successor.placement)) break;
    const offset = successor.placement.offsetMinutes - step.placement.durationMinutes;
    const displaced = { ...step, placement: step.placement.shiftedBy(offset - step.placement.offsetMinutes) };
    result[fromIndex - reverseIndex - 1] = displaced;
    successor = displaced;
  }
  let previous: T | undefined;
  for (const [index, step] of steps.entries()) {
    if (index < fromIndex) continue;
    if (previous !== undefined) {
      if (previous.placement.precedes(step.placement)) break;
      const displaced = {
        ...step,
        placement: step.placement.shiftedBy(previous.placement.endMinutes - step.placement.offsetMinutes),
      };
      result[index] = displaced;
      previous = displaced;
    } else previous = step;
  }
  return result;
}

/**
 * Move an authored step to an absolute grid offset, settling both neighbours.
 * The input is an ordered timeline; crossing offsets preserves step sequence.
 * Boundary refusals leave every input step unchanged.
 */
export function moveStep<T extends { placement: StepPlacement }>(
  steps: readonly T[],
  fromIndex: number,
  offsetMinutes: number,
): T[] {
  const step = stepAt(steps, fromIndex);
  const placement = step.placement.shiftedBy(offsetMinutes - step.placement.offsetMinutes);
  const candidates = steps.map((entry, index) => {
    if (index === fromIndex) return { ...entry, placement };
    // Keep sequence identity across a drop; settle requires ordered offsets.
    if (
      (index < fromIndex && entry.placement.offsetMinutes > offsetMinutes) ||
      (index > fromIndex && entry.placement.offsetMinutes < offsetMinutes)
    ) {
      return { ...entry, placement: entry.placement.shiftedBy(offsetMinutes - entry.placement.offsetMinutes) };
    }
    return entry;
  });
  return settle(candidates, fromIndex);
}

/** Reorder whole steps, preserving durations, positional gaps, and the overall span. */
export function reorderStep<T extends { placement: StepPlacement }>(
  steps: readonly T[],
  fromIndex: number,
  toIndex: number,
): T[] {
  const moved = stepAt(steps, fromIndex);
  stepAt(steps, toIndex);
  if (fromIndex === toIndex) return [...steps];
  const first = Math.min(fromIndex, toIndex);
  const last = Math.max(fromIndex, toIndex);
  const result = [...steps];
  result.splice(fromIndex, 1);
  result.splice(toIndex, 0, moved);
  let offset = stepAt(steps, first).placement.offsetMinutes;
  for (let index = first; index <= last; index++) {
    const step = stepAt(result, index);
    result[index] = { ...step, placement: step.placement.shiftedBy(offset - step.placement.offsetMinutes) };
    const gap =
      index < last ? stepAt(steps, index + 1).placement.offsetMinutes - stepAt(steps, index).placement.endMinutes : 0;
    offset += step.placement.durationMinutes + gap;
  }
  return result;
}

function stepAt<T>(steps: readonly T[], index: number): T {
  const step = steps[index];
  if (!Number.isInteger(index) || step === undefined) {
    throw new PreconditionFailedError("Step edit requires an integer index within the steps array");
  }
  return step;
}

/** Resize a step, clamped to one scheduling increment, then settle neighbours. */
export function resizeStep<T extends { placement: StepPlacement }>(
  steps: readonly T[],
  fromIndex: number,
  durationMinutes: number,
): T[] {
  const step = stepAt(steps, fromIndex);
  if (!Number.isFinite(durationMinutes)) {
    throw new PreconditionFailedError("Step resize requires finite duration minutes");
  }
  const placement = step.placement.resizedTo(Math.max(ENVELOPE_DURATION_INCREMENT_MINUTES, durationMinutes));
  return settle(
    steps.map((entry, index) => (index === fromIndex ? { ...entry, placement } : entry)),
    fromIndex,
  );
}
